import type { AssignCaseParams } from "~~/server/api/case/assign.post";
import type { CreateCaseParams } from "~~/server/api/case/create.post";
import type { ForceAcceptParams } from "~~/server/api/case/force-accept.post";
import type { ForceAssignParams } from "~~/server/api/case/force-assign.post";
import type { CreateCompanyParams } from "~~/server/api/company/create.post";
import type { UpdateCompanyParams } from "~~/server/api/company/update.put";
import type { CreateNewParams } from "~~/server/api/news/create.post";
import type { updateNewsParams } from "~~/server/api/news/update.put";
import type { CreateUserParams } from "~~/server/api/user/create.post";
import type { ForceUpdateUserParams } from "~~/server/api/user/force-update.put";
import type { MeParams } from "~~/server/api/user/me.post";
import type { ICoord, RecordStatus, RemarkType } from "~~/types/information";
import type { searchSftpPolicyParams } from "~~/server/api/sftp-policy/search.post";
import type { ISftpPolicy } from "~~/types/sftp-policy";
import type { updateSftpPolicyRemarkParams } from "~~/server/api/sftp-policy/update-remark.post";
import type { ExtractPolicyDataParams } from "~~/server/api/sftp-policy/extract-policy-data.post";
import type { CreateFromExtractParams } from "~~/server/api/sftp-policy/create-from-extract.post";
import type { AcceptCaseParams } from "~~/server/api/case/accept.post";
import type { CloseCaseParams } from "~~/server/api/case/close.post";
import type { FinishCaseParams } from "~~/server/api/case/finish.post";
import type { ForceCloseParams } from "~~/server/api/case/force-close.post";
import type { ForceFinishParams } from "~~/server/api/case/force-finish.post";
import type { ForceStartParams } from "~~/server/api/case/force-start.post";
import type { ForceUpdateParams } from "~~/server/api/case/force-update.post";
import type { ReassignParams } from "~~/server/api/case/reassign.post";
import type { SmsSurveyParams } from "~~/server/api/case/sms-survey.post";
import type { StartCaseParams } from "~~/server/api/case/start.post";
import type { DeleteUserParams } from "~~/server/api/user/delete.delete";
import type { UpdateUserParams } from "~~/server/api/user/update.put";
import type { UpdateUserLocationParams } from "~~/server/api/user/update-location.put";
import type { ForceUpdatePricingParams } from "~~/server/api/pricing/force-update.put";
import type { CopyCompanyPricingParams } from "~~/server/api/pricing/copy/company.post";
import type { CopyStateParams } from "~~/server/api/pricing/copy/state.post";
import type { CalculatePaymentParams } from "~~/server/api/pricing/calculate-payment.post";
import type RecordStatistic from "~~/server/database/entity/record-statistic";
import type { searchRecordParams } from "~~/server/api/record/search.post";
import type { ReconcileRecordsParams } from "~~/server/api/record/reconcile.post";
import type { IRecord } from "~~/types/record";
import type { CreateCloseReasonParams } from "~~/server/api/settings/close-reasons.post";
import type { UpdateCloseReasonParams } from "~~/server/api/settings/close-reasons/[id].put";
import type { CreateProviderServiceParams } from "~~/server/api/settings/provider-services.post";
import type { UpdateProviderServiceParams } from "~~/server/api/settings/provider-services/[id].put";
import type { UpdateConfigParams } from "~~/server/api/settings/configs/[id].put";
import type { IConfig } from "~~/types/config";
import type { ICloseReason, IProviderService } from "~~/types/case";
import type { ICaseRequest } from "~~/types/case-request";
import type { IRankedWorkshop, IWorkshop } from "~~/types/workshop";
import type {
  ClientEmailReason,
  IClientEmailPendingAssignValues,
  IClientEmailPreviousDestination,
  IClientEmailPlan,
  IClientEmailSendResult,
} from "~~/types/client-email";
import type { IFleetVehicle } from "~~/types/fleet-vehicle";
import type { GetNearbyProviderParams } from "~~/server/api/user/provider/get-nearby-provider.post";
import type { GetAllProvidersParams } from "~~/server/api/user/provider/get-all-providers.post";
import type { GetAllBasesParams } from "~~/server/api/user/provider/get-all-bases.post";
import type { GetAllProvidersLeaderParams } from "~~/server/api/user/provider/get-all-providers-leader.post";
import type { GetNearbyBasesParams } from "~~/server/api/user/provider/get-nearby-bases.post";
import type { GetNearbyProviderLeaderParams } from "~~/server/api/user/provider/get-nearby-provider-leader.post";
import type { GetLocationParams } from "~~/server/api/location/user/get.post";
import type { UpdateSelfLocationParams } from "~~/server/api/location/user/update-self.put";
import type { CreateRemarkParams } from "~~/server/api/remark/create.post";
import type { GetRemarksParams } from "~~/server/api/remark/index.get";
import { DeleteRemarkParams } from "~~/server/api/remark/[id].delete";

const useNetwork = defineStore(`network`, () => {
  const forceUpdateUser = async (param: {
    body: Partial<ForceUpdateUserParams>;
  }) => {
    return $fetch("/api/user/force-update", {
      method: "PUT",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const createUser = async (param: { body: CreateUserParams }) => {
    return $fetch("/api/user/create", {
      method: "POST",
      headers: await getAuthHeader(),
      body: param.body,
    });
  };

  const getProfile = async (params?: { body?: MeParams }) => {
    return $fetch("/api/user/me", {
      method: "POST",
      headers: await getAuthHeader(),
      body: params?.body || {},
    });
  };

  const createCase = async (param: { body: Partial<CreateCaseParams> }) => {
    return $fetch("/api/case/create", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const assignCase = async (param: { body: Partial<AssignCaseParams> }) => {
    return $fetch("/api/case/assign", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const forceAssignCase = async (param: {
    body: Partial<ForceAssignParams>;
  }) => {
    return $fetch("/api/case/force-assign", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const forceAcceptCase = async (param: {
    body: Partial<ForceAcceptParams>;
  }) => {
    return $fetch("/api/case/force-accept", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const createCompany = async (params: { body: CreateCompanyParams }) => {
    return $fetch("/api/company/create", {
      headers: await getAuthHeader(),
      method: "POST",
      body: params.body,
    });
  };

  const updateCompany = async (params: { body: UpdateCompanyParams }) => {
    return $fetch("/api/company/update", {
      headers: await getAuthHeader(),
      method: "PUT",
      body: params.body,
    });
  };

  const createNews = async (params: { body: CreateNewParams }) => {
    return $fetch("/api/news/create", {
      headers: await getAuthHeader(),
      method: "POST",
      body: params.body,
    });
  };

  const updateNews = async (params: { body: updateNewsParams }) => {
    return $fetch("/api/news/update", {
      headers: await getAuthHeader(),
      method: "PUT",
      body: params.body,
    });
  };

  const deleteNews = async (params: { id: string }) => {
    return $fetch("/api/news/delete", {
      headers: await getAuthHeader(),
      method: "DELETE",
      body: { id: params.id },
    });
  };

  /** Google maps API */
  const getDistance = async ({
    origin,
    destination,
  }: {
    origin: ICoord;
    destination: ICoord;
  }) => {
    return $fetch<{
      destination_addresses: string[];
      origin_addresses: string[];
      rows: {
        elements: {
          distance: {
            text: string;
            value: number;
          };
          duration: {
            text: string;
            value: number;
          };
          status: string;
        }[];
      }[];
      status: string;
    }>("/api/maps/distance-matrix", {
      headers: await getAuthHeader(),
      method: "GET",
      query: {
        destinations: `${destination.latitude},${destination.longitude}`,
        origins: `${origin.latitude},${origin.longitude}`,
        mode: "driving",
      },
    });
  };

  const getCoordinatesFromAddress = async (address: string) => {
    return $fetch<{
      results: Array<{
        formatted_address: string;
        geometry: {
          location: {
            lat: number;
            lng: number;
          };
        };
        address_components: any[];
      }>;
      status: string;
    }>("/api/maps/get-address-coordinate", {
      headers: await getAuthHeader(),
      method: "GET",
      query: {
        address,
      },
    });
  };

  const getAddressFromCoordinates = async (
    latitude: number,
    longitude: number,
  ) => {
    return $fetch<{
      results: Array<{
        formatted_address: string;
        geometry: {
          location: {
            lat: number;
            lng: number;
          };
        };
        address_components: any[];
      }>;
      status: string;
    }>("/api/maps/get-coordinate-address", {
      headers: await getAuthHeader(),
      method: "GET",
      query: {
        latlng: `${latitude},${longitude}`,
      },
    });
  };

  // Company panel-workshop CRUD (companies/{id}/workshops). The manage-workshops page is the
  // only caller; the public form reads the same data via /api/external/workshops.
  type WorkshopInput = Omit<IWorkshop, "id" | "createdAt" | "updatedAt">;

  // With a pickup pin the workshops come back nearest first with distanceKm / recommended.
  const listCompanyWorkshops = async (
    companyId: string,
    pin?: ICoord | null,
  ) => {
    return $fetch<{ status: string; data: IRankedWorkshop[] }>(
      `/api/company/${companyId}/workshops`,
      {
        headers: await getAuthHeader(),
        method: "GET",
        query: pin ? { lat: pin.latitude, lng: pin.longitude } : undefined,
      },
    );
  };

  // Client case emails (docs/CLIENT_ASSIGNMENT_EMAIL.md): what would be sent for this case, and
  // the send itself for the post-save / post-close modal. The assign modal sends through the
  // assign / force-assign body's `email` field instead.
  const previewClientEmails = async (params: {
    caseId: string;
    reason: ClientEmailReason;
    pending?: IClientEmailPendingAssignValues;
    previous?: IClientEmailPreviousDestination;
  }) => {
    return $fetch<{ status: string; data: IClientEmailPlan }>(
      `/api/case/${params.caseId}/client-email/preview`,
      {
        headers: await getAuthHeader(),
        method: "POST",
        body: {
          reason: params.reason,
          pending: params.pending,
          previous: params.previous,
        },
      },
    );
  };

  const sendClientEmails = async (params: {
    caseId: string;
    reason: Exclude<ClientEmailReason, "assign" | "finish">;
    send: boolean;
    photoUrls?: string[];
    remark?: string;
    previous?: IClientEmailPreviousDestination;
  }) => {
    return $fetch<{ status: string; data: IClientEmailSendResult }>(
      `/api/case/${params.caseId}/client-email/send`,
      {
        headers: await getAuthHeader(),
        method: "POST",
        body: {
          reason: params.reason,
          send: params.send,
          photoUrls: params.photoUrls,
          remark: params.remark,
          previous: params.previous,
        },
      },
    );
  };

  const createCompanyWorkshop = async (params: {
    companyId: string;
    body: WorkshopInput;
  }) => {
    return $fetch<{ status: string; data: IWorkshop }>(
      `/api/company/${params.companyId}/workshops`,
      { headers: await getAuthHeader(), method: "POST", body: params.body },
    );
  };

  const updateCompanyWorkshop = async (params: {
    companyId: string;
    workshopId: string;
    body: Partial<WorkshopInput>;
  }) => {
    return $fetch<{ status: string; data: IWorkshop }>(
      `/api/company/${params.companyId}/workshops/${params.workshopId}`,
      { headers: await getAuthHeader(), method: "PUT", body: params.body },
    );
  };

  const deleteCompanyWorkshop = async (params: {
    companyId: string;
    workshopId: string;
  }) => {
    return $fetch<{ status: string; data: { id: string } }>(
      `/api/company/${params.companyId}/workshops/${params.workshopId}`,
      { headers: await getAuthHeader(), method: "DELETE" },
    );
  };

  // Company fleet-vehicle CRUD (companies/{id}/vehicles). The manage-vehicles page is the only
  // admin caller; the MAN fleet-owner mobile app reads the same data scoped to the user's company.
  type FleetVehicleInput = Omit<
    IFleetVehicle,
    "id" | "createdAt" | "updatedAt"
  >;

  const listCompanyVehicles = async (companyId: string) => {
    return $fetch<{ status: string; data: IFleetVehicle[] }>(
      `/api/company/${companyId}/vehicles`,
      { headers: await getAuthHeader(), method: "GET" },
    );
  };

  const createCompanyVehicle = async (params: {
    companyId: string;
    body: FleetVehicleInput;
  }) => {
    return $fetch<{ status: string; data: IFleetVehicle }>(
      `/api/company/${params.companyId}/vehicles`,
      { headers: await getAuthHeader(), method: "POST", body: params.body },
    );
  };

  const updateCompanyVehicle = async (params: {
    companyId: string;
    vehicleId: string;
    body: Partial<FleetVehicleInput>;
  }) => {
    return $fetch<{ status: string; data: IFleetVehicle }>(
      `/api/company/${params.companyId}/vehicles/${params.vehicleId}`,
      { headers: await getAuthHeader(), method: "PUT", body: params.body },
    );
  };

  const deleteCompanyVehicle = async (params: {
    companyId: string;
    vehicleId: string;
  }) => {
    return $fetch<{ status: string; data: { id: string } }>(
      `/api/company/${params.companyId}/vehicles/${params.vehicleId}`,
      { headers: await getAuthHeader(), method: "DELETE" },
    );
  };

  const searchSftpPolicy = async (params: { body: searchSftpPolicyParams }) => {
    return $fetch<{
      status: string;
      data: ISftpPolicy[];
      total: number;
    }>("/api/sftp-policy/search", {
      headers: await getAuthHeader(),
      method: "POST",
      body: params.body,
    });
  };

  const updateSftpPolicyRemark = async (params: {
    body: updateSftpPolicyRemarkParams;
  }) => {
    return $fetch<{
      status: string;
    }>("/api/sftp-policy/update-remark", {
      headers: await getAuthHeader(),
      method: "POST",
      body: params.body,
    });
  };

  const extractPolicyData = async (params: {
    body: ExtractPolicyDataParams;
  }) => {
    return $fetch<{
      status: string;
      data: ISftpPolicy;
    }>("/api/sftp-policy/extract-policy-data", {
      headers: await getAuthHeader(),
      method: "POST",
      body: params.body,
    });
  };

  const createPolicyFromExtract = async (params: {
    body: CreateFromExtractParams;
  }) => {
    return $fetch<{
      status: string;
      data: ISftpPolicy;
    }>("/api/sftp-policy/create-from-extract", {
      headers: await getAuthHeader(),
      method: "POST",
      body: params.body,
    });
  };

  const searchRecord = async (params: { body: searchRecordParams }) => {
    return $fetch<{
      status: string;
      data: IRecord[];
      total: number;
    }>("/api/record/search", {
      headers: await getAuthHeader(),
      method: "POST",
      body: params.body,
    });
  };

  const reconcileRecords = async (params: { body: ReconcileRecordsParams }) => {
    return $fetch<{
      status: string;
      scanned: number;
      alreadySynced: number;
      backfilled: number;
      failed: number;
      orphans: Array<{ id: string; regNum?: string }>;
    }>("/api/record/reconcile", {
      headers: await getAuthHeader(),
      method: "POST",
      body: params.body,
    });
  };

  const startCase = async (param: { body: Partial<StartCaseParams> }) => {
    return $fetch("/api/case/start", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const smsSurvey = async (param: { body: Partial<SmsSurveyParams> }) => {
    return $fetch("/api/case/sms-survey", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const acceptCase = async (param: { body: Partial<AcceptCaseParams> }) => {
    return $fetch("/api/case/accept", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const closeCase = async (param: { body: Partial<CloseCaseParams> }) => {
    return $fetch("/api/case/close", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const finishCase = async (param: { body: Partial<FinishCaseParams> }) => {
    return $fetch("/api/case/finish", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const forceCloseCase = async (param: { body: Partial<ForceCloseParams> }) => {
    return $fetch("/api/case/force-close", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const forceFinishCase = async (param: {
    body: Partial<ForceFinishParams>;
  }) => {
    return $fetch("/api/case/force-finish", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const forceStartCase = async (param: { body: Partial<ForceStartParams> }) => {
    return $fetch("/api/case/force-start", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const forceUpdateCase = async (param: {
    body: Partial<ForceUpdateParams>;
  }) => {
    return $fetch("/api/case/force-update", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const reassignCase = async (param: { body: Partial<ReassignParams> }) => {
    return $fetch("/api/case/reassign", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const updateUserLocation = async (param: {
    body: Partial<UpdateUserLocationParams>;
  }) => {
    return $fetch("/api/user/update-location", {
      headers: await getAuthHeader(),
      method: "PUT",
      body: param.body,
    });
  };

  const updateUser = async (param: { body: Partial<UpdateUserParams> }) => {
    return $fetch("/api/user/update", {
      headers: await getAuthHeader(),
      method: "PUT",
      body: param.body,
    });
  };

  const deleteUser = async (param: { body: Partial<DeleteUserParams> }) => {
    return $fetch("/api/user/delete", {
      headers: await getAuthHeader(),
      method: "DELETE",
      body: param.body,
    });
  };

  const getUsersByIds = async (param: { body: { userIds: string[] } }) => {
    return $fetch("/api/user/get-by-ids", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const getPolicies = async () => {
    return $fetch("/api/policy", {
      headers: await getAuthHeader(),
      method: "GET",
    });
  };

  const getFaultCodes = async () => {
    return $fetch("/api/fault-code", {
      headers: await getAuthHeader(),
      method: "GET",
    });
  };

  const sendNotification = async () => {
    return $fetch("/api/notification/send", {
      headers: await getAuthHeader(),
      method: "POST",
      body: {},
    });
  };

  const forceUpdatePrice = async (param: {
    body: Partial<ForceUpdatePricingParams>;
  }) => {
    return $fetch("/api/pricing/force-update", {
      headers: await getAuthHeader(),
      method: "PUT",
      body: param.body,
    });
  };

  const copyCompanyPrice = async (param: {
    body: Partial<CopyCompanyPricingParams>;
  }) => {
    return $fetch("/api/pricing/copy/company", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const copyStatePrice = async (param: { body: Partial<CopyStateParams> }) => {
    return $fetch("/api/pricing/copy/state", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const getCompanyPricingTemplates = async (companyId: string) => {
    return $fetch(`/api/company/${companyId}/pricing-templates`, {
      headers: await getAuthHeader(),
      method: "GET",
    });
  };

  const createCompanyPricingTemplate = async (companyId: string, body: any) => {
    return $fetch(`/api/company/${companyId}/pricing-templates`, {
      headers: await getAuthHeader(),
      method: "POST",
      body,
    });
  };

  const updatePricingTemplate = async (templateId: string, body: any) => {
    return $fetch(`/api/pricing/templates/${templateId}`, {
      headers: await getAuthHeader(),
      method: "PUT",
      body,
    });
  };

  const deletePricingTemplate = async (templateId: string) => {
    return $fetch(`/api/pricing/templates/${templateId}`, {
      headers: await getAuthHeader(),
      method: "DELETE",
    });
  };

  const copyPricingTemplates = async (body: {
    fromCompanyId: string;
    toCompanyId: string;
    templateIds?: string[];
  }) => {
    return $fetch("/api/pricing/templates/copy-templates", {
      headers: await getAuthHeader(),
      method: "POST",
      body,
    });
  };

  const getCaseStatistics = async () => {
    return $fetch<{ statistic: RecordStatistic }>("/api/statistic/case", {
      headers: await getAuthHeader(),
    });
  };

  const refreshCaseStatistics = async () => {
    return $fetch("/api/statistic/refresh", {
      method: "POST",
      headers: await getAuthHeader(),
    });
  };

  const getDailyCaseStatistics = async (params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    return $fetch<{
      dailyStatistic: {
        created: number;
        assigned: number;
        reassigned: number;
        accepted: number;
        ongoing: number;
        finished: number;
        closed: number;
        total: number;
        date: string;
        startDate: string;
        endDate: string;
        updatedAt: Date;
      };
    }>("/api/statistic/daily-cases", {
      headers: await getAuthHeader(),
      method: "GET",
      query: params,
    });
  };

  const getDailyDetailedCaseStatistics = async () => {
    return $fetch<{
      dailyDetailedStatistic: {
        breakdown: Array<{
          clientId: string;
          clientName: string;
          clientType: string;
          stateId: string;
          stateName: string;
          providerId: string | null;
          providerName: string | null;
          providerType: string | null;
          created: number;
          assigned: number;
          reassigned: number;
          accepted: number;
          ongoing: number;
          finished: number;
          closed: number;
          total: number;
        }>;
        totalCases: number;
        date: string;
        updatedAt: Date;
      };
    }>("/api/statistic/daily-cases-detailed", {
      headers: await getAuthHeader(),
      method: "GET",
    });
  };

  const getCasesBreakdown = async (startDate: string, endDate: string) => {
    return $fetch<{
      casesBreakdown: {
        breakdown: Array<{
          regNum: string;
          clientId: string;
          clientName: string;
          clientType: string;
          stateId: string;
          stateName: string;
          providerId: string | null;
          providerName: string | null;
          providerType: string | null;
          policyId: string | null;
          policyName: string | null;
          created: number;
          assigned: number;
          reassigned: number;
          accepted: number;
          ongoing: number;
          finished: number;
          closed: number;
          total: number;
        }>;
        totalCases: number;
        startDate: string;
        endDate: string;
        updatedAt: Date;
      };
    }>("/api/statistic/cases-breakdown", {
      headers: await getAuthHeader(),
      method: "GET",
      query: {
        startDate,
        endDate,
      },
    });
  };

  const getServices = async () => {
    return $fetch("/api/service", {
      headers: await getAuthHeader(),
      method: "GET",
    });
  };

  const syncDb = async (param: {
    body: { collectionName: string; id: string; data: any };
  }) => {
    return $fetch("/api/db-sync", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const deleteCompany = async (param: { body: { id: string } }) => {
    return $fetch("/api/company/delete", {
      headers: await getAuthHeader(),
      method: "DELETE",
      body: param.body,
    });
  };

  const calculatePayment = async (param: { body: CalculatePaymentParams }) => {
    return $fetch("/api/pricing/calculate-payment", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  // Close Reasons functions
  const getCloseReasons = async () => {
    return $fetch<{ status: string; data: ICloseReason[] }>(
      "/api/settings/close-reasons",
      {
        headers: await getAuthHeader(),
        method: "GET",
      },
    );
  };

  const createCloseReason = async (param: {
    body: CreateCloseReasonParams;
  }) => {
    return $fetch("/api/settings/close-reasons", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };

  const updateCloseReason = async (param: {
    id: string;
    body: Partial<UpdateCloseReasonParams>;
  }) => {
    return $fetch(`/api/settings/close-reasons/${param.id}`, {
      headers: await getAuthHeader(),
      method: "PUT",
      body: param.body,
    });
  };

  const deleteCloseReason = async (param: { id: string }) => {
    return $fetch(`/api/settings/close-reasons/${param.id}`, {
      headers: await getAuthHeader(),
      method: "DELETE",
    });
  };

  // Provider Services functions
  const getProviderServices = async () => {
    return $fetch<{ status: string; data: IProviderService[] }>(
      "/api/settings/provider-services",
      {
        headers: await getAuthHeader(),
        method: "GET",
      },
    );
  };
  const createProviderService = async (param: {
    body: CreateProviderServiceParams;
  }) => {
    return $fetch("/api/settings/provider-services", {
      headers: await getAuthHeader(),
      method: "POST",
      body: param.body,
    });
  };
  const updateProviderService = async (param: {
    id: string;
    body: Partial<UpdateProviderServiceParams>;
  }) => {
    return $fetch(`/api/settings/provider-services/${param.id}`, {
      headers: await getAuthHeader(),
      method: "PUT",
      body: param.body,
    });
  };
  const deleteProviderService = async (param: { id: string }) => {
    return $fetch(`/api/settings/provider-services/${param.id}`, {
      headers: await getAuthHeader(),
      method: "DELETE",
    });
  };

  // General configuration functions
  const getConfigs = async () => {
    return $fetch<{ status: string; data: IConfig[] }>(
      "/api/settings/configs",
      {
        headers: await getAuthHeader(),
        method: "GET",
      },
    );
  };

  const updateConfig = async (param: {
    id: string;
    body: UpdateConfigParams;
  }) => {
    return $fetch(`/api/settings/configs/${param.id}`, {
      headers: await getAuthHeader(),
      method: "PUT",
      body: param.body,
    });
  };

  // Case Request functions
  const createCaseRequest = async (param: { body: Partial<ICaseRequest> }) => {
    return $fetch("/api/case-request", {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const updateCaseRequest = async (param: {
    id: string;
    body: Partial<ICaseRequest>;
  }) => {
    return $fetch(`/api/case-request/${param.id}`, {
      method: "PUT",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const deleteCaseRequest = async (param: { id: string }) => {
    return $fetch(`/api/case-request/${param.id}`, {
      method: "DELETE",
      headers: await getAuthHeader(),
    });
  };

  const lockCaseRequest = async (param: { id: string }) => {
    return $fetch(`/api/case-request/${param.id}/lock`, {
      method: "POST",
      headers: await getAuthHeader(),
    });
  };

  const unlockCaseRequest = async (param: { id: string }) => {
    return $fetch(`/api/case-request/${param.id}/unlock`, {
      method: "POST",
      headers: await getAuthHeader(),
    });
  };

  const acceptCaseRequest = async (param: { id: string; body: any }) => {
    return $fetch(`/api/case-request/${param.id}/accept`, {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getNearbyProvider = async (param: {
    body: Partial<GetNearbyProviderParams>;
  }) => {
    return $fetch("/api/user/provider/get-nearby-provider", {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getAllProviders = async (param: {
    body: Partial<GetAllProvidersParams>;
  }) => {
    return $fetch("/api/user/provider/get-all-providers", {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getNearbyBases = async (param: {
    body: Partial<GetNearbyBasesParams>;
  }) => {
    return $fetch("/api/user/provider/get-nearby-bases", {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getNearbyProviderLeader = async (param: {
    body: Partial<GetNearbyProviderLeaderParams>;
  }) => {
    return $fetch("/api/user/provider/get-nearby-provider-leader", {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getAllBases = async (param: { body: Partial<GetAllBasesParams> }) => {
    return $fetch("/api/user/provider/get-all-bases", {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getAllProvidersLeader = async (param: {
    body: Partial<GetAllProvidersLeaderParams>;
  }) => {
    return $fetch("/api/user/provider/get-all-providers-leader", {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getCaseLogs = async (param: {
    caseId: string;
    page?: number;
    per_page?: number;
  }) => {
    return $fetch<{
      logs: Array<{
        id: string;
        createdAt: number;
        action: string;
        targetType: string;
        targetId: string;
        data: string;
        tag: string;
        modifiedBy: {
          id: string;
          displayName: string;
          photoUrl: string;
          company: {
            id: string;
            name: string;
          } | null;
        } | null;
      }>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
      has_more: boolean;
    }>(`/api/case/${param.caseId}/logs`, {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        page: param.page,
        per_page: param.per_page,
      },
    });
  };

  const getUserLogs = async (param: {
    userId: string;
    page?: number;
    per_page?: number;
  }) => {
    return $fetch<{
      logs: Array<{
        id: string;
        createdAt: number;
        action: string;
        targetType: string;
        targetId: string;
        data: string;
        tag: string;
        modifiedBy: {
          id: string;
          displayName: string;
          photoUrl: string;
          company: {
            id: string;
            name: string;
          } | null;
        } | null;
      }>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
      has_more: boolean;
    }>(`/api/user/${param.userId}/logs`, {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        page: param.page,
        per_page: param.per_page,
      },
    });
  };

  const getCompanyLogs = async (param: {
    companyId: string;
    page?: number;
    per_page?: number;
  }) => {
    return $fetch<{
      logs: Array<{
        id: string;
        createdAt: number;
        action: string;
        targetType: string;
        targetId: string;
        data: string;
        tag: string;
        modifiedBy: {
          id: string;
          displayName: string;
          photoUrl: string;
          company: {
            id: string;
            name: string;
          } | null;
        } | null;
      }>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
      has_more: boolean;
    }>(`/api/company/${param.companyId}/logs`, {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        page: param.page,
        per_page: param.per_page,
      },
    });
  };

  const getCaseRequestLogs = async (param: {
    requestId: string;
    page?: number;
    per_page?: number;
  }) => {
    return $fetch<{
      logs: Array<{
        id: string;
        createdAt: number;
        action: string;
        targetType: string;
        targetId: string;
        data: string;
        tag: string;
        modifiedBy: {
          id: string;
          displayName: string;
          photoUrl: string;
          company: {
            id: string;
            name: string;
          } | null;
        } | null;
      }>;
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
      has_more: boolean;
    }>(`/api/case-request/${param.requestId}/logs`, {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        page: param.page,
        per_page: param.per_page,
      },
    });
  };

  const updateSelfLiveLocation = async (param: {
    body: UpdateSelfLocationParams;
  }) => {
    return $fetch("/api/location/user/update-self", {
      method: "PUT",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getProvidersLocation = async (param: {
    body: Partial<GetLocationParams>;
  }) => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        name: string;
        contactNum: string | null;
        location: { latitude: number; longitude: number } | null;
        active: boolean;
        companyId: string | null;
        companyName: string | null;
        cases: Array<{
          id: string;
          regNum: string;
          clientRegNum: string;
          status: RecordStatus;
          createdAt: string;
        }>;
      }>;
    }>("/api/location/user/get", {
      method: "POST",
      body: param.body,
      headers: await getAuthHeader(),
    });
  };

  const getUsersListing = async (params?: {
    includeLeader?: boolean;
    companyId?: string;
  }) => {
    return $fetch("/api/listing/user", {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        includeLeader: params?.includeLeader,
        companyId: params?.companyId,
      },
    });
  };

  const getProvidersListing = async () => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        name: string;
        email: string;
        displayName: string;
        companyName: string;
        accountType: string;
      }>;
    }>("/api/listing/provider", {
      method: "GET",
      headers: await getAuthHeader(),
    });
  };

  const getCompaniesListing = async () => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        name: string;
      }>;
    }>("/api/listing/company", {
      method: "GET",
      headers: await getAuthHeader(),
    });
  };

  const getCompaniesWithPricing = async () => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        name: string;
      }>;
    }>("/api/listing/company-with-pricing", {
      method: "GET",
      headers: await getAuthHeader(),
    });
  };

  const getCaseRegNumListing = async (params: {
    query: string;
    limit?: number;
  }) => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        regNum: string;
      }>;
      total: number;
    }>("/api/listing/case-regnum", {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        q: params.query,
        limit: params.limit,
      },
    });
  };

  const getClientRegNumListing = async (params: {
    query: string;
    limit?: number;
  }) => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        regNum: string;
      }>;
      total: number;
    }>("/api/listing/client-regnum", {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        q: params.query,
        limit: params.limit,
      },
    });
  };

  const getCustomerContactNumListing = async (params: {
    query: string;
    limit?: number;
  }) => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        contactNumber: string;
      }>;
      total: number;
    }>("/api/listing/customer-contact-num", {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        q: params.query,
        limit: params.limit,
      },
    });
  };

  const getCaseIdListing = async (params: {
    query: string;
    limit?: number;
  }) => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
      }>;
      total: number;
    }>("/api/listing/case-id", {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        q: params.query,
        limit: params.limit,
      },
    });
  };

  const getVehicleRegNumListing = async (params: {
    query: string;
    limit?: number;
  }) => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        regNum: string;
      }>;
      total: number;
    }>("/api/listing/vehicle-regnum", {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        q: params.query,
        limit: params.limit,
      },
    });
  };

  const getCallerNameListing = async (params: {
    query: string;
    limit?: number;
  }) => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        callerName: string;
      }>;
      total: number;
    }>("/api/listing/caller-name", {
      method: "GET",
      headers: await getAuthHeader(),
      query: {
        q: params.query,
        limit: params.limit,
      },
    });
  };

  // Remark functions
  const createRemark = async (param: { body: CreateRemarkParams }) => {
    return $fetch<{
      status: string;
      data: {
        id: string;
        createdAt: number;
        content: string;
        type: RemarkType;
        creator: {
          id: string;
          name: string;
        };
      };
    }>("/api/remark/create", {
      method: "POST",
      headers: await getAuthHeader(),
      body: param.body,
    });
  };

  const getRemarks = async (param: { query: GetRemarksParams }) => {
    return $fetch<{
      status: string;
      data: Array<{
        id: string;
        createdAt: number;
        content: string;
        type: RemarkType;
        creator: {
          id: string;
          name: string;
        };
      }>;
    }>("/api/remark", {
      method: "GET",
      headers: await getAuthHeader(),
      query: param.query,
    });
  };

  const deleteRemark = async (param: { id: string }) => {
    return $fetch<{
      status: string;
      data: {};
    }>(`/api/remark/${param.id}`, {
      method: "DELETE",
      headers: await getAuthHeader(),
    });
  };

  return {
    forceUpdateUser,
    createUser,
    getProfile,
    createCase,
    assignCase,
    forceAssignCase,
    forceAcceptCase,
    createCompany,
    updateCompany,
    createNews,
    updateNews,
    deleteNews,
    getDistance,
    getCoordinatesFromAddress,
    getAddressFromCoordinates,
    listCompanyWorkshops,
    previewClientEmails,
    sendClientEmails,
    createCompanyWorkshop,
    updateCompanyWorkshop,
    deleteCompanyWorkshop,
    listCompanyVehicles,
    createCompanyVehicle,
    updateCompanyVehicle,
    deleteCompanyVehicle,
    searchSftpPolicy,
    updateSftpPolicyRemark,
    extractPolicyData,
    createPolicyFromExtract,
    searchRecord,
    reconcileRecords,
    startCase,
    smsSurvey,
    acceptCase,
    closeCase,
    finishCase,
    forceCloseCase,
    forceFinishCase,
    forceStartCase,
    forceUpdateCase,
    reassignCase,
    updateUserLocation,
    updateUser,
    deleteUser,
    getUsersByIds,
    getPolicies,
    getFaultCodes,
    sendNotification,
    forceUpdatePrice,
    copyCompanyPrice,
    copyStatePrice,
    getCompanyPricingTemplates,
    createCompanyPricingTemplate,
    updatePricingTemplate,
    deletePricingTemplate,
    copyPricingTemplates,

    getServices,
    syncDb,
    deleteCompany,
    calculatePayment,
    getCaseStatistics,
    refreshCaseStatistics,
    getDailyCaseStatistics,
    getDailyDetailedCaseStatistics,
    getCasesBreakdown,
    getCloseReasons,
    createCloseReason,
    updateCloseReason,
    deleteCloseReason,
    getProviderServices,
    createProviderService,
    updateProviderService,
    deleteProviderService,
    getConfigs,
    updateConfig,
    // Case Request
    createCaseRequest,
    updateCaseRequest,
    deleteCaseRequest,
    lockCaseRequest,
    unlockCaseRequest,
    acceptCaseRequest,
    getNearbyProvider,
    getAllProviders,
    getAllBases,
    getAllProvidersLeader,
    getNearbyBases,
    getNearbyProviderLeader,
    getCaseLogs,
    getUserLogs,
    getCompanyLogs,
    getCaseRequestLogs,
    updateSelfLiveLocation,
    getProvidersLocation,
    getUsersListing,
    getProvidersListing,
    getCompaniesListing,
    getCompaniesWithPricing,
    getCaseRegNumListing,
    getClientRegNumListing,
    getCustomerContactNumListing,
    getCaseIdListing,
    getVehicleRegNumListing,
    getCallerNameListing,
    // Remarks
    createRemark,
    getRemarks,
    deleteRemark,
  };
});

export default useNetwork;
